import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Camera, Ruler, ShoppingCart, Plus, Minus, Gamepad2, ChevronLeft, ChevronRight, X, ZoomIn, Share2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useSite } from '../lib/siteContext';
import { colorRule, matrixKey, readMatrixKey, validateColorOrder, colorTotalQty } from '../lib/colorMoq';

const ProductDetail = ({ products, activeCategory, selectedProductId, gsm, onAddToCart, onBack }) => {
  const { data } = useSite();
  const branding = data?.branding;
  const product = useMemo(() => {
    if (!products?.length) return null;
    return products.find((p) => p.id === selectedProductId) || products.find((p) => p.category_slug === activeCategory) || products[0];
  }, [products, activeCategory, selectedProductId]);
  const sizeChart = useMemo(() => (data?.size_charts || []).find((sc) => sc.id === product?.size_chart_id), [data, product]);

  const [photoIndex, setPhotoIndex] = useState(0);
  const [showPhotos, setShowPhotos] = useState(true);
  const [showSizeChart, setShowSizeChart] = useState(false);
  const [qtyMatrix, setQtyMatrix] = useState({});
  const [lightboxOpen, setLightboxOpen] = useState(false);

  React.useEffect(() => {
    setQtyMatrix({});
    setPhotoIndex(0);
  }, [activeCategory, gsm, product?.id]);

  if (!product) {
    return <div className="p-8 text-center text-neutral-500">No products yet. Add one from admin panel.</div>;
  }

  const updateQty = (colorIndex, size, delta) => {
    const key = matrixKey(colorIndex, size);
    setQtyMatrix((prev) => {
      const cur = prev[key] || 0;
      return { ...prev, [key]: Math.max(0, cur + delta) };
    });
  };

  const setQty = (colorIndex, size, value) => {
    const key = matrixKey(colorIndex, size);
    setQtyMatrix((prev) => ({ ...prev, [key]: Math.max(0, parseInt(value) || 0) }));
  };

  const totalQty = Object.values(qtyMatrix).reduce((a, b) => a + b, 0);
  const price = totalQty >= product.min_qty ? product.price_high : product.price_low;
  const totalPrice = totalQty * price;
  const colorOrderErrors = validateColorOrder(product, qtyMatrix);

  const orderItems = Object.entries(qtyMatrix)
    .filter(([, q]) => q > 0)
    .map(([key, qty]) => {
      const { colorIndex, size } = readMatrixKey(key);
      const color = product.colors?.[colorIndex]?.name || 'Unknown';
      return { color, size, qty };
    });

  const lineMinQty = (product.colors || []).reduce((sum, c, idx) => {
    const total = colorTotalQty(c, idx, product.sizes, qtyMatrix);
    return total > 0 ? sum + colorRule(c).moq : sum;
  }, 0) || 1;

  const handleAddToCart = () => {
    if (totalQty === 0) {
      toast.error('Please select at least 1 piece');
      return;
    }
    if (colorOrderErrors.length) {
      toast.error(colorOrderErrors[0]);
      return;
    }
    onAddToCart({ productName: product.name, totalQty, totalPrice, price, items: orderItems, minQty: lineMinQty });
    setQtyMatrix({});
    toast.success(`Added ${totalQty} pcs to cart`);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/?product=${product.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: product.name, url });
        return;
      }
    } catch { /* user cancelled or unsupported; fall through to copy */ }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Product link copied!');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const photos = product.photos?.length ? product.photos : ['https://via.placeholder.com/800x600?text=No+Image'];

  return (
    <div className="grid w-full min-w-0 lg:grid-cols-[minmax(0,1fr),340px] gap-4 px-3 md:px-6 py-4">
      <div className="min-w-0">
        <div className="mb-3">
          {onBack && (
            <button data-testid="product-back-to-products-button" onClick={onBack} className="mb-2 inline-flex items-center gap-1 text-[12px] font-medium text-neutral-600 hover:text-orange-600">
              <ChevronLeft className="w-3.5 h-3.5" /> Back to products
            </button>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="min-w-0 break-words text-lg md:text-xl font-bold text-neutral-900">{product.name}</h1>
            <span data-testid="product-colour-rules-badge" className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 border border-sky-200">
              Colour-wise stock & MOQ
            </span>
          </div>
          <p className="break-words text-[13px] text-neutral-600 mt-0.5">{product.description}</p>
        </div>

        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <button data-testid="product-photos-toggle-button" onClick={() => { setShowPhotos(!showPhotos); setShowSizeChart(false); }} className={`text-[12px] flex items-center gap-1 px-2.5 py-1.5 rounded border font-medium transition-colors ${showPhotos ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500'}`}>
            <Camera className="w-3.5 h-3.5" /> Photos
          </button>
          <button data-testid="product-size-chart-toggle-button" onClick={() => { setShowSizeChart(!showSizeChart); setShowPhotos(false); }} className={`text-[12px] flex items-center gap-1 px-2.5 py-1.5 rounded border font-medium transition-colors ${showSizeChart ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500'}`}>
            <Ruler className="w-3.5 h-3.5" /> Size Chart
          </button>
          <a href="#game" className="text-[12px] flex items-center gap-1 px-2.5 py-1.5 rounded border border-neutral-300 hover:border-orange-500 text-neutral-700 hover:text-orange-600 font-medium">
            <Gamepad2 className="w-3.5 h-3.5" /> Game
          </a>
          <button data-testid="product-share-button" onClick={handleShare} className="text-[12px] flex items-center gap-1 px-2.5 py-1.5 rounded border border-neutral-300 hover:border-orange-500 text-neutral-700 hover:text-orange-600 font-medium">
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
          <Button data-testid="product-add-to-cart-button" onClick={handleAddToCart} className="ml-auto h-8 bg-orange-500 hover:bg-orange-600 text-white gap-1 text-[13px]">
            <ShoppingCart className="w-3.5 h-3.5" /> Add to Cart {totalQty > 0 && `(${totalQty})`}
          </Button>
        </div>

        {showPhotos && (
          <div className="mb-4 border border-neutral-200 rounded-lg overflow-hidden bg-neutral-50">
            <div className="relative aspect-[4/3] md:aspect-[16/9] bg-neutral-100">
              <button data-testid="product-photo-zoom-button" onClick={() => setLightboxOpen(true)} className="group w-full h-full cursor-zoom-in">
                <img src={photos[photoIndex]} alt={product.name} className="w-full h-full object-contain" />
                <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/10 transition-colors">
                  <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 drop-shadow transition-opacity" />
                </span>
              </button>
              <button onClick={() => setPhotoIndex((photoIndex - 1 + photos.length) % photos.length)} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 rounded-full grid place-items-center shadow hover:bg-white transition-colors">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setPhotoIndex((photoIndex + 1) % photos.length)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 rounded-full grid place-items-center shadow hover:bg-white transition-colors">
                <ChevronRight className="w-4 h-4" />
              </button>
              <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[11px] px-2 py-0.5 rounded">
                {photoIndex + 1} / {photos.length}
              </div>
            </div>
            <div className="flex gap-1.5 p-2 overflow-x-auto bg-white">
              {photos.map((p, i) => (
                <button key={i} onClick={() => setPhotoIndex(i)} className={`shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all bg-neutral-100 ${i === photoIndex ? 'border-orange-500' : 'border-transparent hover:border-neutral-300'}`}>
                  <img src={p} alt="" className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          </div>
        )}

        {lightboxOpen && (
          <div data-testid="product-photo-lightbox" className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={() => setLightboxOpen(false)}>
            <button data-testid="product-photo-lightbox-close-button" onClick={() => setLightboxOpen(false)} className="absolute top-4 right-4 w-9 h-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white">
              <X className="w-5 h-5" />
            </button>
            {photos.length > 1 && (
              <button onClick={(e) => { e.stopPropagation(); setPhotoIndex((photoIndex - 1 + photos.length) % photos.length); }} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white">
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}
            <img src={photos[photoIndex]} alt={product.name} className="max-w-[92vw] max-h-[88vh] object-contain" onClick={(e) => e.stopPropagation()} />
            {photos.length > 1 && (
              <button onClick={(e) => { e.stopPropagation(); setPhotoIndex((photoIndex + 1) % photos.length); }} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white">
                <ChevronRight className="w-6 h-6" />
              </button>
            )}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-[12px]">
              {photoIndex + 1} / {photos.length}
            </div>
          </div>
        )}

        {showSizeChart && (
          <div className="mb-4 border border-neutral-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-neutral-50 border-b border-neutral-200 text-[13px] font-semibold">{sizeChart ? sizeChart.name : 'Size Chart'} (in inches)</div>
            <table className="w-full text-[12px]">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-3 py-1.5 text-left border-b border-neutral-200">Size</th>
                  <th className="px-3 py-1.5 text-left border-b border-neutral-200">Chest</th>
                  <th className="px-3 py-1.5 text-left border-b border-neutral-200">Length</th>
                  <th className="px-3 py-1.5 text-left border-b border-neutral-200">Shoulder</th>
                </tr>
              </thead>
              <tbody>
                {sizeChart ? sizeChart.rows.map((r, i) => (
                  <tr key={r.size || i} className={i % 2 ? 'bg-neutral-50/40' : ''}>
                    <td className="px-3 py-1.5 font-medium">{r.size}</td>
                    <td className="px-3 py-1.5">{r.chest}</td>
                    <td className="px-3 py-1.5">{r.length}</td>
                    <td className="px-3 py-1.5">{r.shoulder}</td>
                  </tr>
                )) : (product.sizes || []).map((s, i) => (
                  <tr key={s} className={i % 2 ? 'bg-neutral-50/40' : ''}>
                    <td className="px-3 py-1.5 font-medium">{s}</td>
                    <td className="px-3 py-1.5">{20 + i * 1.5}”</td>
                    <td className="px-3 py-1.5">{26 + i}”</td>
                    <td className="px-3 py-1.5">{17 + i * 0.5}”</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-orange-50 border-b border-orange-100 flex items-center justify-between flex-wrap gap-1">
            <div className="text-[13px] font-semibold text-orange-900">
              {activeCategory === 'oversize' ? `OS${gsm}` : (product.name?.split(' ')[0] || '')}
            </div>
            <div className="text-[11px] text-orange-800 flex items-center gap-2 flex-wrap">
              <span>Ready Stock: MOQ match karo phir +1 jitne chahein</span>
              <span>·</span>
              <span>Made to Order: MOQ + Step ke multiples</span>
            </div>
          </div>
          <div className="divide-y divide-neutral-100">
            {(product.colors || []).map((c, colorIndex) => {
              const rule = colorRule(c);
              const isReady = rule.stockStatus === 'ready';
              const statusLabel = isReady ? 'Ready Stock' : 'Made to Order';
              const colorTotal = colorTotalQty(c, colorIndex, product.sizes, qtyMatrix);
              const short = colorTotal > 0 && colorTotal < rule.moq;
              return (
                <div key={`${c.name}-${colorIndex}`} data-testid={`colour-row-${colorIndex}`} className="p-3 hover:bg-orange-50/30">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="w-4 h-4 shrink-0 rounded-full border border-neutral-300 shadow-inner" style={{ background: c.hex }} />
                    <span className="text-[13px] text-neutral-800 font-semibold">{c.name}</span>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${isReady ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                      {statusLabel}
                    </span>
                    <span data-testid={`colour-rule-${colorIndex}`} className="text-[10px] font-medium text-neutral-500">
                      {isReady ? `Min ${rule.moq} pcs · phir jitne chahein +1` : `MOQ ${rule.moq} · Step +${rule.step}`}
                    </span>
                    {colorTotal > 0 && (
                      <span className={`ml-auto text-[11px] font-semibold ${short ? 'text-amber-700' : 'text-emerald-700'}`}>
                        {colorTotal} pcs{short ? ` · ${rule.moq - colorTotal} more` : ' ✓'}
                      </span>
                    )}
                  </div>

                  <div data-testid={`colour-${colorIndex}-delivery-message`} className="text-[10px] text-neutral-500 mb-2">
                    {isReady ? 'Dispatched within 24 hours' : 'Order will be ready within 7 days'}
                  </div>

                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                    {(product.sizes || []).map((s) => {
                      const key = matrixKey(colorIndex, s);
                      const val = qtyMatrix[key] || 0;
                      return (
                        <div key={s} className="min-w-0">
                          <div className="text-[10px] text-neutral-500 font-medium text-center mb-0.5">{s}</div>
                          <div className={`flex items-center border rounded overflow-hidden bg-white ${val > 0 ? 'border-orange-300' : 'border-neutral-200'}`}>
                            <button data-testid={`colour-${colorIndex}-${s}-decrease-button`} onClick={() => updateQty(colorIndex, s, -1)} className="w-6 h-7 shrink-0 grid place-items-center text-neutral-500 hover:bg-neutral-100">
                              <Minus className="w-3 h-3" />
                            </button>
                            <input data-testid={`colour-${colorIndex}-${s}-quantity-input`} type="number" min="0" step="1" value={val} onChange={(e) => setQty(colorIndex, s, e.target.value)} className="w-full min-w-0 h-7 text-center text-[12px] focus:outline-none focus:bg-orange-50" />
                            <button data-testid={`colour-${colorIndex}-${s}-increase-button`} onClick={() => updateQty(colorIndex, s, 1)} className="w-6 h-7 shrink-0 grid place-items-center text-neutral-500 hover:bg-neutral-100">
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {product.fabric && (
          <div className="mt-4 p-3 rounded-lg bg-neutral-50 border border-neutral-200 text-[13px] text-neutral-700">
            <strong>Fabric:</strong> {product.fabric}
          </div>
        )}
      </div>

      <div className="lg:sticky lg:top-4 h-fit">
        <div className="border border-neutral-200 rounded-lg bg-white overflow-hidden">
          <div className="px-3 py-2 bg-neutral-900 text-white flex items-center justify-between">
            <div className="text-[13px] font-semibold flex items-center gap-2">
              <ShoppingCart className="w-4 h-4" /> Order Summary
            </div>
            <button data-testid="product-copy-order-button" onClick={handleAddToCart} className="text-[11px] px-2 py-0.5 rounded bg-white/10 hover:bg-white/20 border border-white/20">Copy Order</button>
          </div>
          <div className="p-3">
            {totalQty === 0 ? (
              <div className="text-center py-6 text-[13px] text-neutral-500">
                <ShoppingCart className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
                No items selected
              </div>
            ) : (
              <>
                <div className="space-y-1 max-h-56 overflow-y-auto">
                  {Object.entries(qtyMatrix)
                    .filter(([, q]) => q > 0)
                    .map(([key, qty]) => {
                      const { colorIndex, size } = readMatrixKey(key);
                      const color = product.colors?.[colorIndex]?.name || 'Unknown';
                      return (
                        <div key={key} className="flex items-center justify-between text-[12px] py-1 border-b border-neutral-100">
                          <span className="text-neutral-700">{color} / {size}</span>
                          <span className="font-medium">{qty} pcs</span>
                        </div>
                      );
                    })}
                </div>
                <div className="mt-3 pt-3 border-t border-neutral-200 space-y-1 text-[13px]">
                  <div className="flex justify-between text-neutral-600"><span>Total qty</span><span className="font-semibold text-neutral-900">{totalQty} pcs</span></div>
                  <div className="flex justify-between text-neutral-600"><span>Price/pc</span><span>₹{price}</span></div>
                  <div className="flex justify-between text-neutral-600"><span>Subtotal</span><span>₹{totalPrice.toLocaleString('en-IN')}</span></div>
                  <div className="flex justify-between text-neutral-600"><span>GST (5%)</span><span>₹{Math.round(totalPrice * 0.05).toLocaleString('en-IN')}</span></div>
                  <div className="flex justify-between text-[15px] pt-1 border-t border-neutral-100 mt-1"><span className="font-semibold">Grand Total</span><span className="font-bold text-orange-600">₹{(totalPrice + Math.round(totalPrice * 0.05)).toLocaleString('en-IN')}</span></div>
                </div>
                <div className="mt-3 space-y-1.5">
                  <input data-testid="product-discount-code-input" placeholder="🏷️ Have a discount code?" className="w-full text-[12px] px-2.5 py-1.5 border border-neutral-200 rounded focus:border-orange-500 focus:outline-none" />
                  {colorOrderErrors.length > 0 && (
                    <div data-testid="colour-moq-error-message" className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
                      {colorOrderErrors[0]}
                    </div>
                  )}
                  <Button
                    data-testid="order-summary-add-to-cart-button"
                    onClick={handleAddToCart}
                    className="w-full mt-1 bg-orange-500 hover:bg-orange-600 text-white gap-1"
                  >
                    <ShoppingCart className="w-4 h-4" /> Add to Cart ({totalQty} pcs)
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {totalQty > 0 && document.getElementById('floating-actions-slot') && createPortal(
        <Button
          data-testid="floating-add-to-cart-button"
          onClick={handleAddToCart}
          className="lg:hidden h-auto gap-1.5 px-3.5 py-2.5 rounded-full shadow-lg text-[12px] font-semibold bg-orange-500 hover:bg-orange-600 text-white"
        >
          <ShoppingCart className="w-4 h-4" /> Add to Cart ({totalQty} pcs)
        </Button>,
        document.getElementById('floating-actions-slot')
      )}
    </div>
  );
};

export default ProductDetail;
